import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, Eye, EyeSlash, Wallet, Receipt, HandCoins, ClipboardText, Clock } from "@phosphor-icons/react";

const portalModules = [
  { label: "Gaji", icon: Wallet },
  { label: "Absensi", icon: Clock },
  { label: "Reimbursement", icon: Receipt },
  { label: "Kasbon", icon: HandCoins },
  { label: "RFQ", icon: ClipboardText },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Selamat datang, ${user.name}`);
      navigate(user.role === "admin" ? "/admin" : "/attendance");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#00364e] text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-2xl p-3 shadow-xl">
            <img src="/logo-samitra.webp" alt="Samitra Karya Nusantara" className="h-20 w-auto" />
          </div>
        </div>
        <div>
          <Buildings size={36} weight="duotone" className="text-[#9fccec] mb-6" />
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#9fccec] mb-3">
            Portal Internal Perusahaan
          </p>
          <h2 className="font-heading text-4xl font-bold tracking-tight leading-[1.1] mb-4 text-white">
            PORTAL PT SAMITRA<br />KARYA NUSANTARA
          </h2>
          <p className="text-white/60 max-w-md leading-relaxed">
            Portal karyawan & admin untuk pengelolaan kebutuhan internal perusahaan secara digital, terintegrasi, dan transparan.
          </p>
          <div className="border-t border-white/10 mt-8 pt-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-md">
              {portalModules.map((m) => (
                <div key={m.label} className="flex items-center gap-2.5">
                  <m.icon size={18} weight="duotone" className="text-[#9fccec] shrink-0" />
                  <span className="text-sm text-white/70">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="text-white/40 text-sm">© 2026 Samitra Group. All rights reserved.</p>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-sm animate-fade-up rounded-xl bg-white border border-[#E2E8F0]/80 shadow-sm p-6 sm:p-8">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo-samitra.webp" alt="Samitra Karya Nusantara" className="h-16 w-auto" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1 text-[#0b1c30]">Masuk ke Akun</h1>
          <p className="text-[#64748B] mb-8 text-sm">Gunakan email & password yang diberikan.</p>

          <form onSubmit={(e) => doLogin(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nama@samitragroup.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200 ease-apple"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#00364e] hover:bg-[#1b4d68] text-white" disabled={loading} data-testid="login-submit-button">
              {loading ? "Memproses…" : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
